// Which UI shell to mount: the Flutter-replica phone UI, or the wide desktop UI.
//
// The two UIs share ALL logic (everything under src/core). They differ only in
// presentation, so this is the single place that decides which one renders.
//
//   • Native (Capacitor Android/iOS) -> always 'mobile'
//   • Browser                        -> 'desktop', unless the window is narrow
//   • localStorage.uiMode            -> manual override, wins over both
//
// Keep this free of React imports below the hook so non-UI code can call it.

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

export const MOBILE_MAX_WIDTH = 900;
const OVERRIDE_KEY = 'uiMode';

export function isNativePlatform() {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
}

export function getUiOverride() {
  try {
    const v = localStorage.getItem(OVERRIDE_KEY);
    return v === 'mobile' || v === 'desktop' ? v : null;
  } catch (e) {
    return null;
  }
}

export function setUiOverride(mode) {
  try {
    if (mode === 'mobile' || mode === 'desktop') localStorage.setItem(OVERRIDE_KEY, mode);
    else localStorage.removeItem(OVERRIDE_KEY);
  } catch (e) { /* ignore */ }
}

export function detectUiMode() {
  const forced = getUiOverride();
  if (forced) return forced;
  if (isNativePlatform()) return 'mobile';
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_MAX_WIDTH
    ? 'mobile'
    : 'desktop';
}

// Live UI mode — re-evaluates on resize so dragging a browser window narrow
// switches to the phone layout (and back).
export function useUiMode() {
  const [mode, setMode] = useState(detectUiMode);
  useEffect(() => {
    if (isNativePlatform() || getUiOverride()) return undefined;
    const onResize = () => setMode(detectUiMode());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mode;
}
